'use client';

import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Candidate as SearchCandidate, TimelineItem } from "@/types/search";
import type { Candidate as JobCandidate } from "@/types/candidate";
import { searchApi } from "@/api/search";
import { publicApi } from "@/api/public";

// Create a union type that can handle both candidate types
type FlexibleCandidate = SearchCandidate | JobCandidate;

// Add CandidateNote interface
export interface CandidateNote {
  id: string;
  jobCandidateId: string;
  organisationId: string;
  note: string; // Text content
  mentions?: Array<{ employeeId: string; employeeName: string }>; // Mentions in the note
  createdBy: string; // UUID
  createdAt: string; // Timestamp
  updatedAt: string; // Timestamp
  
  // Included association
  creator?: {
    id: string; // UUID
    name: string;
    email: string;
  }
}

interface CandidateState {
  selectedCandidate: SearchCandidate | null;
  isLoading: boolean;
  error: string | null;
  timeline?: TimelineItem[];
  timelineCount?: number;
  notesCount?: number;
  aiInterviewScheduledAt?: string;
  hasCompletedAiInterview?: boolean;
}

interface CandidateContextType {
  candidateState: CandidateState;
  selectedCandidate: SearchCandidate | null;
  getCandidateById: (id: string | number | null, jobId?: string, isPublic?: boolean) => Promise<void>;
  clearSelectedCandidate: () => void;
  // Multi-select functionality
  selectedCandidates: FlexibleCandidate[];
  setSelectedCandidates: (candidates: FlexibleCandidate[]) => void;
  toggleCandidateSelection: (candidate: FlexibleCandidate) => void;
  clearSelectedCandidates: () => void;
  // Action modal functionality
  isActionModalOpen: boolean;
  openActionModal: () => void;
  closeActionModal: () => void;
  selectedTemplate: string | null;
  setSelectedTemplate: (template: string | null) => void;
  // Notes functionality
  notes: CandidateNote[];
  isLoadingNotes: boolean;
  errorNotes: string | null;
  fetchNotes: (candidateId: string, jobId?: string) => Promise<void>;
  addNote: (candidateId: string, content: string, jobId?: string, mentions?: Array<{ employeeId: string; employeeName: string }>) => Promise<void>;
  updateNote: (candidateId: string, noteId: string, content: string, jobId?: string, mentions?: Array<{ employeeId: string; employeeName: string }>) => Promise<void>;
  // Tab counts
  timeline?: TimelineItem[];
  timelineCount?: number;
  notesCount?: number;
  aiInterviewScheduledAt?: string;
  hasCompletedAiInterview?: boolean;
}

const CandidateContext = createContext<CandidateContextType | undefined>(undefined);

export function CandidateProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [candidateState, setCandidateState] = useState<CandidateState>({
    selectedCandidate: null,
    isLoading: false,
    error: null,
    timeline: undefined,
    timelineCount: undefined,
    notesCount: undefined,
    aiInterviewScheduledAt: undefined,
    hasCompletedAiInterview: undefined,
  });
  const [selectedCandidate, setSelectedCandidate] = useState<SearchCandidate | null>(null);
  
  // Multi-select state
  const [selectedCandidates, setSelectedCandidates] = useState<FlexibleCandidate[]>([]);
  
  // Action modal state
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  
  // Notes state
  const [notes, setNotes] = useState<CandidateNote[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [errorNotes, setErrorNotes] = useState<string | null>(null);

  const getCandidateById = useCallback(async (id: string | number | null, jobId?: string, isPublic?: boolean) => {
    if (id === null || id === undefined) {
      setCandidateState(prev => ({ ...prev, selectedCandidate: null }));
      setSelectedCandidate(null);
      return;
    }

    setCandidateState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      let response;
      
      if (isPublic) {
        // In public context, treat the id as a referral code
        const publicResponse = await publicApi.getCandidateByRef(id.toString());
        response = {
          candidate: publicResponse.candidate,
          timeline: publicResponse.timeline,
          timelineCount: publicResponse.timelineCount,
          notesCount: publicResponse.notesCount,
          aiInterviewScheduledAt: publicResponse.aiInterviewScheduledAt,
          hasCompletedAiInterview: publicResponse.hasCompletedAiInterview,
        };
      } else {
        // Use protected API for authenticated contexts - always include timeline
        response = await searchApi.getCandidateById(id.toString(), jobId, true);
      }
          
      setCandidateState({
        selectedCandidate: response.candidate,
        isLoading: false,
        error: null,
        timeline: response.timeline,
        timelineCount: response.timelineCount,
        notesCount: response.notesCount,
        aiInterviewScheduledAt: response.aiInterviewScheduledAt,
        hasCompletedAiInterview: response.hasCompletedAiInterview,
      });
      setSelectedCandidate(response.candidate);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load candidate";
      setCandidateState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      toast({
        description: errorMessage,
        variant: "error",
      });
    }
  }, [toast]);

  const clearSelectedCandidate = useCallback(() => {
    setCandidateState(prev => ({ ...prev, selectedCandidate: null }));
  }, []);
  
  // Multi-select functions
  const toggleCandidateSelection = useCallback((candidate: FlexibleCandidate) => {
    setSelectedCandidates(prev => {
      const isSelected = prev.some(c => c.id === candidate.id);
      if (isSelected) {
        return prev.filter(c => c.id !== candidate.id);
      } else {
        return [...prev, candidate];
      }
    });
  }, []);
  
  const clearSelectedCandidates = useCallback(() => {
    setSelectedCandidates([]);
  }, []);
  
  // Action modal functions
  const openActionModal = useCallback(() => {
    setIsActionModalOpen(true);
  }, []);
  
  const closeActionModal = useCallback(() => {
    setIsActionModalOpen(false);
    setSelectedTemplate(null);
    setSelectedCandidates([]); // Clear selected candidates when closing the modal
  }, []);
  
  // Notes functions
  const fetchNotes = useCallback(async (candidateId: string, jobId?: string) => {
    if (!candidateId || !jobId) {
      setErrorNotes("Missing candidate ID or job ID");
      return;
    }
    
    setIsLoadingNotes(true);
    setErrorNotes(null);
    
    try {
      // Use the API function instead of direct fetch
      const data = await searchApi.fetchCandidateNotes(jobId, candidateId);
      setNotes(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load notes";
      setErrorNotes(errorMessage);
      toast({
        description: errorMessage,
        variant: "warning",
      });
      
      // Fallback to notes from candidate object if API fails
      if (selectedCandidate?.notes && Array.isArray(selectedCandidate.notes)) {
        setNotes(selectedCandidate.notes as unknown as CandidateNote[]);
      } else {
        setNotes([]);
      }
    } finally {
      setIsLoadingNotes(false);
    }
  }, [toast, selectedCandidate]);

  const addNote = useCallback(async (
    candidateId: string, 
    content: string, 
    jobId?: string,
    mentions?: Array<{ employeeId: string; employeeName: string }>
  ) => {
    if (!candidateId || !content.trim() || !jobId) {
      if (!jobId) {
        toast({
          variant: "warning",
          description: "Cannot add note: Missing job context",
        });
      }
      return;
    }
    
    try {
      // Use the API function instead of direct fetch
      const newNoteData = await searchApi.addCandidateNote(jobId, candidateId, content.trim(), mentions);
      
      // Update the local state with the new note
      setNotes(prev => [...prev, newNoteData]);
      
      toast({
        description: "Note added successfully",
      });
      
      // Refresh notes from the server
      fetchNotes(candidateId, jobId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to add note";
      toast({
        description: errorMessage,
        variant: "warning",
      });
    }
  }, [toast, fetchNotes]);

  const updateNote = useCallback(async (
    candidateId: string,
    noteId: string,
    content: string,
    jobId?: string,
    mentions?: Array<{ employeeId: string; employeeName: string }>
  ) => {
    if (!candidateId || !noteId || !content.trim() || !jobId) {
      if (!jobId) {
        toast({
          variant: "warning",
          description: "Cannot update note: Missing job context",
        });
      }
      return;
    }
    
    try {
      // Use the API function to update the note
      const updatedNoteData = await searchApi.updateCandidateNote(jobId, candidateId, noteId, content.trim(), mentions);
      
      // Update the local state with the updated note
      setNotes(prev => prev.map(note => note.id === noteId ? updatedNoteData : note));
      
      toast({
        description: "Note updated successfully",
      });
      
      // Refresh notes from the server to ensure consistency
      fetchNotes(candidateId, jobId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update note";
      toast({
        description: errorMessage,
        variant: "warning",
      });
      throw error; // Re-throw to allow component to handle it
    }
  }, [toast, fetchNotes]);

  return (
    <CandidateContext.Provider
      value={{
        candidateState,
        getCandidateById,
        selectedCandidate,
        clearSelectedCandidate,
        // Multi-select
        selectedCandidates,
        setSelectedCandidates,
        toggleCandidateSelection,
        clearSelectedCandidates,
        // Action modal
        isActionModalOpen,
        openActionModal,
        closeActionModal,
        selectedTemplate,
        setSelectedTemplate,
        // Notes
        notes,
        isLoadingNotes,
        errorNotes,
        fetchNotes,
        addNote,
        updateNote,
        // Tab counts
        timeline: candidateState.timeline,
        timelineCount: candidateState.timelineCount,
        notesCount: candidateState.notesCount,
        aiInterviewScheduledAt: candidateState.aiInterviewScheduledAt,
        hasCompletedAiInterview: candidateState.hasCompletedAiInterview,
      }}
    >
      {children}
    </CandidateContext.Provider>
  );
}

export function useCandidate() {
  const context = useContext(CandidateContext);
  if (!context) {
    throw new Error("useCandidate must be used within CandidateProvider");
  }
  return context;
}